/**
 * Server-authoritative onboarding sync.
 *
 * The workspace-setup wizard must show exactly once per account. It used to be
 * gated purely on a localStorage flag, so it reappeared on a new device or after
 * the browser cleared storage. This module reconciles the local onboarding store
 * with the server:
 *
 *   - On sign-in, GET the server state. If the server says "completed", mark the
 *     local store completed (and hydrate the saved profile) so the wizard never
 *     shows. If the server has no record but the local store IS completed (a user
 *     who onboarded before this was server-side), POST to backfill the server.
 *   - When the user finishes onboarding, POST the completed profile so it sticks
 *     across devices.
 *
 * The fetch helpers never throw: on any failure they resolve null / no-op and we
 * fall back to the local flag, so a flaky network can't wedge a signed-in user
 * out of their workspace.
 */
import { useEffect, useRef, useState } from "react";

import { apiUrl } from "./apiBase";
import { stepsForAccountType } from "./components/onboarding/MorrowOnboarding.logic";
import {
  getOnboardingProfile,
  resetOnboardingProfile,
  useOnboardingProfile,
  writeOnboardingProfile,
  type MorrowOnboardingProfile,
} from "./onboardingStore";

/**
 * The onboarding profile lives under a single (non-user-keyed) localStorage key,
 * so a second account signing in on a shared browser would otherwise inherit the
 * first account's `completed` flag — and we'd wrongly backfill it to the server.
 * Stamp the owner of the locally-stored profile so we can discard stale state
 * from a different account before reconciling.
 */
const ONBOARDING_OWNER_KEY = "morrow:onboarding:owner";

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Clear the local profile if it belongs to a different account, then claim it. */
function reconcileOnboardingOwner(userId: string): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    const previousOwner = storage.getItem(ONBOARDING_OWNER_KEY);
    if (previousOwner && previousOwner !== userId) {
      resetOnboardingProfile();
    }
    storage.setItem(ONBOARDING_OWNER_KEY, userId);
  } catch {
    // Best-effort; worst case we fall back to server-only reconciliation.
  }
}

export interface ServerOnboardingState {
  readonly completed: boolean;
  readonly profile: Partial<MorrowOnboardingProfile>;
  /** Per-step completion flags from the server ({ "profile": true, … }). */
  readonly steps: Record<string, boolean>;
  /** True once the welcome thread (knowledge record) has been seeded server-side. */
  readonly knowledgeReady: boolean;
}

function parseStepsMap(value: unknown): Record<string, boolean> {
  if (!value || typeof value !== "object") return {};
  const out: Record<string, boolean> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === true) out[key] = true;
  }
  return out;
}

/** GET the server onboarding state for a user, or null on any failure. */
export async function fetchServerOnboarding(
  userId: string,
  signal?: AbortSignal,
): Promise<ServerOnboardingState | null> {
  if (!userId) return null;
  try {
    const res = await fetch(apiUrl(`/api/morrow/onboarding?userId=${encodeURIComponent(userId)}`), {
      headers: { accept: "application/json" },
      ...(signal ? { signal } : {}),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as {
      completed?: unknown;
      profile?: unknown;
      steps?: unknown;
      knowledgeReady?: unknown;
    };
    return {
      completed: body.completed === true,
      profile:
        body.profile && typeof body.profile === "object"
          ? (body.profile as Partial<MorrowOnboardingProfile>)
          : {},
      steps: parseStepsMap(body.steps),
      knowledgeReady: body.knowledgeReady === true,
    };
  } catch {
    return null;
  }
}

/** POST the account's onboarding state to the server. Best-effort; never throws. */
export async function persistServerOnboarding(
  userId: string,
  profile: MorrowOnboardingProfile,
  steps?: Record<string, boolean>,
): Promise<void> {
  if (!userId) return;
  try {
    await fetch(apiUrl("/api/morrow/onboarding"), {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({
        userId,
        completed: profile.completed,
        profile,
        ...(steps ? { steps } : {}),
      }),
    });
  } catch {
    // Best-effort; the wizard may reappear on another device until this lands.
  }
}

/** All steps for a profile's account type, flagged done — used at completion. */
function completedStepsMap(profile: MorrowOnboardingProfile): Record<string, boolean> {
  const steps = stepsForAccountType(profile.accountType);
  const map: Record<string, boolean> = {};
  for (const step of steps) map[step] = true;
  return map;
}

/**
 * Reconcile the local onboarding store with the server for a signed-in user.
 *
 * Returns `hydrated`: false until the server has been consulted (or there's no
 * user to consult). Callers gate the wizard on `hydrated` so an already-onboarded
 * user never sees a flash of onboarding before the server answer arrives.
 */
export interface OnboardingServerSync {
  readonly hydrated: boolean;
  /** True once the welcome thread (knowledge record) exists server-side. */
  readonly knowledgeReady: boolean;
  /** Per-step completion flags from the server. */
  readonly steps: Record<string, boolean>;
}

export function useOnboardingServerSync(
  userId: string | null,
  enabled: boolean,
): OnboardingServerSync {
  const profile = useOnboardingProfile();
  const [hydrated, setHydrated] = useState(false);
  const [knowledgeReady, setKnowledgeReady] = useState(false);
  const [steps, setSteps] = useState<Record<string, boolean>>({});
  // Guards the completion POST so we push exactly once per completed transition.
  const pushedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !userId) {
      setHydrated(false);
      setKnowledgeReady(false);
      setSteps({});
      pushedRef.current = false;
      return;
    }
    let cancelled = false;
    setHydrated(false);
    pushedRef.current = false;
    // Discard any leftover onboarding profile from a different account on this
    // browser before we decide whether to backfill it to the server.
    reconcileOnboardingOwner(userId);
    void (async () => {
      const server = await fetchServerOnboarding(userId);
      if (cancelled) return;
      if (server) {
        setKnowledgeReady(server.knowledgeReady);
        setSteps(server.steps);
      }
      if (server?.completed) {
        // Server is the source of truth: adopt its profile and mark complete so
        // the wizard is skipped on this device.
        writeOnboardingProfile({ ...server.profile, completed: true });
        pushedRef.current = true;
      } else if (getOnboardingProfile().completed) {
        // Local finished before the server knew — backfill so it sticks.
        const local = getOnboardingProfile();
        await persistServerOnboarding(userId, local, completedStepsMap(local));
        if (cancelled) return;
        pushedRef.current = true;
      }
      if (!cancelled) setHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, enabled]);

  // Push completion to the server the first time onboarding finishes this session,
  // stamping every step done so the DB record reflects the full flag set.
  useEffect(() => {
    if (!enabled || !userId || !hydrated) return;
    if (profile.completed && !pushedRef.current) {
      pushedRef.current = true;
      setSteps(completedStepsMap(profile));
      void persistServerOnboarding(userId, profile, completedStepsMap(profile));
    }
  }, [enabled, userId, hydrated, profile]);

  return { hydrated, knowledgeReady, steps };
}
